import { Dialog as ChakraDialog, Portal } from "@chakra-ui/react";

import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import CloseButton, {
  type CloseButtonProps as UiKitCloseButtonProps,
} from "@ui-kit/form/CloseButton/CloseButton.web";
import {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useId,
  type ComponentProps,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { useTranslation } from "@shared/utils/internationalization";
import { type BoxProps } from "@ui-kit/layout/Box/Box.web";
import type {
  ModalBodySharedProps,
  ModalHeaderSharedProps,
  ModalSharedProps,
} from "./Modal.props";

type ChakraDialogRootProps = ComponentProps<typeof ChakraDialog.Root>;
type ChakraDialogContentProps = ComponentProps<typeof ChakraDialog.Content>;
type ChakraDialogHeaderProps = ComponentProps<typeof ChakraDialog.Header>;
type ChakraDialogBodyProps = ComponentProps<typeof ChakraDialog.Body>;
type ChakraDialogFooterProps = ComponentProps<typeof ChakraDialog.Footer>;
type ChakraDialogCloseTriggerProps = ComponentProps<
  typeof ChakraDialog.CloseTrigger
>;
type ChakraDialogBackdropProps = BoxProps;

type ModalBodyProps = ChakraDialogBodyProps &
  ModalBodySharedProps & {
    variant?: "informational" | "interactive";
  };

export type { ModalBodyProps };

const ModalVariantContext = createContext<"informational" | "interactive">(
  "informational"
);

export type ModalProps = ModalSharedProps &
  Omit<
    ChakraDialogRootProps,
    | "children"
    | "open"
    | "initialFocusEl"
    | "finalFocusEl"
    | "onOpenChange"
    | "scrollBehavior"
    | "closeOnEscape"
    | "closeOnInteractOutside"
    | "preventScroll"
    | "placement"
  > & {
    /** Chakra v2+ forward-compat */
    open?: boolean;
    /** Chakra v1 */
    isCentered?: boolean;
    /** Chakra v2+ */
    centered?: boolean;
    /** Chakra v1 */
    closeOnEsc?: boolean;
    /** Chakra v2+ */
    closeOnEscape?: boolean;
    /** Chakra v1 */
    closeOnOverlayClick?: boolean;
    /** Chakra v3 */
    closeOnInteractOutside?: boolean;
    /** Chakra v1 */
    blockScrollOnMount?: boolean;
    /** Chakra v3 */
    preventScroll?: boolean;
    /** Chakra v3 */
    placement?: ChakraDialogRootProps["placement"];
    /** Chakra v3 */
    scrollBehavior?: ChakraDialogRootProps["scrollBehavior"];
    onExitComplete?: () => void;
    lazyMount?: boolean;
    unmountOnExit?: boolean;
    motionPreset?:
      | "scale"
      | "slide-in-bottom"
      | "slide-in-top"
      | "slide-in-left"
      | "slide-in-right"
      | "none";
    restoreFocus?: boolean;
    role?: "dialog" | "alertdialog";
    persistentElements?: (() => Element | null)[];
    id?: string;
    contentProps?: ChakraDialogContentProps & { "data-testid"?: string };
  };

type ModalType = ModalProps & {
  children: ReactNode;
  modalBodyProps?: ModalBodyProps & {
    "data-testid"?: string;
    style?: CSSProperties;
  };
  modalContentProps?: ChakraDialogContentProps & {
    "data-testid"?: string;
  } & Record<string, unknown>;
  overlayZIndex?: string;
  positionerZIndex?: string;
};

export const bodyBackgroundColor = (variant: ModalProps["variant"]): string => {
  switch (variant) {
    case "informational":
      return "bg";
    case "interactive":
      return "bg.subtle";
    default:
      return "bg";
  }
};

function extractBaseSlots(children: ReactNode): {
  overlay: ReactNode | null;
  content: ReactElement | null;
  otherChildren: ReactNode[];
} {
  const otherChildren: Array<ReactNode> = [];
  let overlay: ReactNode | null = null;
  let content: ReactElement | null = null;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      otherChildren.push(child);
      return;
    }

    if (child.type === Modal.Overlay) {
      overlay = child;
      return;
    }

    if (child.type === Modal.Content) {
      content = child as ReactElement;
      return;
    }

    otherChildren.push(child);
  });

  return { overlay, content, otherChildren };
}

function extractSlots(
  children: ReactNode
): readonly [
  ReactNode | null,
  ReactNode | null,
  ReactNode | null,
  ReactNode | null,
  ReactNode[]
] {
  const otherChildren: Array<ReactNode> = [];
  let footer: ReactNode | null = null;
  let header: ReactNode | null = null;
  let overlay: ReactNode | null = null;
  let body: ReactNode | null = null;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      otherChildren.push(child);
    } else if (child.type === Modal.Footer) {
      footer = child;
    } else if (child.type === Modal.Header) {
      header = child;
    } else if (child.type === Modal.Overlay) {
      overlay = child;
    } else if (child.type === Modal.Body) {
      body = child;
    } else {
      otherChildren.push(child);
    }
  });

  return [overlay, header, body, footer, otherChildren] as const;
}

export default function Modal({
  children,
  modalContentProps = {},
  contentProps,
  initialFocusRef,
  finalFocusRef,
  variant = "informational",
  onClose,
  hideOverlay = false,
  isOpen,
  open,
  isCentered,
  centered,
  closeOnEsc,
  closeOnEscape,
  closeOnOverlayClick,
  closeOnInteractOutside,
  blockScrollOnMount,
  preventScroll,
  placement,
  scrollBehavior,
  onOpenChange,
  size,
  overlayZIndex,
  positionerZIndex,
  onExitComplete,
  lazyMount,
  unmountOnExit,
  motionPreset,
  restoreFocus,
  role,
  persistentElements,
  id,
  ...rest
}: ModalType): ReactElement {
  const openValue = open ?? isOpen;
  const centeredValue = centered ?? isCentered;
  const closeOnEscValue = closeOnEscape ?? closeOnEsc;
  const closeOnOverlayValue =
    closeOnInteractOutside ?? closeOnOverlayClick ?? true;
  const preventScrollValue = preventScroll ?? blockScrollOnMount;

  const mergedContentProps = { ...modalContentProps, ...contentProps };

  const [overlay, originalHeader, body, footer, otherChildren] =
    extractSlots(children);

  const header = isValidElement(originalHeader)
    ? cloneElement(originalHeader as ReactElement<{ onClose?: () => void }>, {
        onClose:
          (originalHeader as ReactElement<{ onClose?: () => void }>).props
            .onClose ?? onClose,
      })
    : null;

  return (
    <Modal.BaseModal
      isOpen={isOpen}
      open={openValue}
      onClose={onClose}
      hideOverlay={hideOverlay}
      scrollBehavior={scrollBehavior}
      isCentered={isCentered}
      centered={centeredValue}
      placement={placement}
      closeOnEsc={closeOnEsc}
      closeOnEscape={closeOnEscValue}
      closeOnOverlayClick={closeOnOverlayClick}
      closeOnInteractOutside={closeOnOverlayValue}
      blockScrollOnMount={blockScrollOnMount}
      preventScroll={preventScrollValue}
      initialFocusRef={initialFocusRef}
      finalFocusRef={finalFocusRef}
      onOpenChange={onOpenChange}
      size={size}
      overlayZIndex={overlayZIndex}
      positionerZIndex={positionerZIndex}
      onExitComplete={onExitComplete}
      lazyMount={lazyMount}
      unmountOnExit={unmountOnExit}
      motionPreset={motionPreset}
      restoreFocus={restoreFocus}
      role={role}
      persistentElements={persistentElements}
      id={id}
      variant={variant}
      {...rest}
    >
      {!hideOverlay && (overlay ?? <Modal.Overlay />)}
      <Modal.Content size={size} {...mergedContentProps}>
        <ModalVariantContext.Provider value={variant}>
          {header}

          {otherChildren}

          {isValidElement(body)
            ? cloneElement(body as ReactElement<ModalBodyProps>, {
                bg: bodyBackgroundColor(variant),
                hasFooter: Boolean(footer),
              })
            : body}

          {footer}
        </ModalVariantContext.Provider>
      </Modal.Content>
    </Modal.BaseModal>
  );
}

Modal.BaseModal = function BaseModal(
  props: ModalProps & {
    children: ReactNode;
    overlayZIndex?: string;
    positionerZIndex?: string;
  }
): ReactElement {
  const {
    children,
    isOpen,
    open,
    onClose,
    hideOverlay,
    scrollBehavior,
    isCentered,
    centered,
    closeOnEsc,
    closeOnEscape,
    closeOnOverlayClick,
    closeOnInteractOutside,
    blockScrollOnMount,
    preventScroll,
    placement,
    initialFocusRef,
    finalFocusRef,
    onOpenChange,
    size,
    modal,
    trapFocus,
    overlayZIndex,
    positionerZIndex,
    portalContainer,
    onExitComplete,
    lazyMount,
    unmountOnExit,
    motionPreset,
    restoreFocus,
    role,
    persistentElements,
    id,
    ...rest
  } = props;

  const reactId = useId();
  const resolvedId = id ?? reactId;

  const openValue = open ?? isOpen;
  const centeredValue = centered ?? isCentered;
  const closeOnEscValue = closeOnEscape ?? closeOnEsc;
  const closeOnOverlayValue =
    closeOnInteractOutside ?? closeOnOverlayClick ?? true;
  const preventScrollValue = preventScroll ?? blockScrollOnMount;
  const placementValue = placement ?? (centeredValue ? "center" : undefined);
  const { overlay, content, otherChildren } = extractBaseSlots(children);

  const contentZIndex = content?.props?.zIndex;
  const effectivePositionerZIndex = positionerZIndex ?? contentZIndex;
  const effectiveOverlayZIndex = overlayZIndex;

  const positionerStyle: CSSProperties = {
    pointerEvents: "auto",
  };

  return (
    <ChakraDialog.Root
      open={openValue}
      modal={modal}
      trapFocus={trapFocus}
      placement={placementValue}
      scrollBehavior={scrollBehavior}
      closeOnEscape={closeOnEscValue}
      closeOnInteractOutside={closeOnOverlayValue}
      preventScroll={preventScrollValue}
      size={size as ChakraDialogRootProps["size"]}
      initialFocusEl={
        initialFocusRef ? () => initialFocusRef.current : undefined
      }
      finalFocusEl={finalFocusRef ? () => finalFocusRef.current : undefined}
      onOpenChange={(details: { open: boolean }) => {
        if (!details.open) {
          onClose?.();
        }
        onOpenChange?.(details);
      }}
      onExitComplete={onExitComplete}
      lazyMount={lazyMount}
      unmountOnExit={unmountOnExit}
      motionPreset={motionPreset}
      restoreFocus={restoreFocus}
      role={role}
      persistentElements={persistentElements}
      id={resolvedId}
      {...rest}
    >
      <Portal container={portalContainer}>
        {!hideOverlay &&
          (overlay ?? <Modal.Overlay zIndex={effectiveOverlayZIndex} />)}
        <ChakraDialog.Positioner
          style={positionerStyle}
          {...(effectivePositionerZIndex && {
            zIndex: effectivePositionerZIndex,
          })}
        >
          {content ? (
            cloneElement(content, { size, ...content.props })
          ) : (
            <Modal.Content size={size}>
              {otherChildren.length ? otherChildren : null}
            </Modal.Content>
          )}
          {content ? otherChildren : null}
        </ChakraDialog.Positioner>
      </Portal>
    </ChakraDialog.Root>
  );
};

Modal.Overlay = function Overlay(
  props: ChakraDialogBackdropProps
): JSX.Element {
  const { zIndex, ...rest } = props;
  return <ChakraDialog.Backdrop {...rest} {...(zIndex && { zIndex })} />;
};

Modal.Content = function Content(
  props: ChakraDialogContentProps & {
    size?: ModalProps["size"];
    css?: object;
  }
): JSX.Element {
  const { size: _size, css: cssProp, ...rest } = props;

  return (
    <ChakraDialog.Content
      bg="bg"
      color="fg"
      css={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        pointerEvents: "auto",
        ...((cssProp as object) || {}),
      }}
      {...rest}
    />
  );
};

Modal.Header = function Header(
  props: ModalHeaderSharedProps & ChakraDialogHeaderProps
): JSX.Element {
  const { onClose, children, closeButtonProps, ...rest } = props;
  const { t } = useTranslation("common");

  return (
    <ChakraDialog.Header
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      lineHeight="30px"
      fontSize={{
        base: "md",
        sm: "xl",
      }}
      padding={{
        base: "md",
        sm: "lg",
      }}
      position="relative"
      borderTopRadius="lg"
      color="fg"
      bg="bg"
      borderBottom="1px solid"
      borderColor="border"
      data-testid="modal-header"
      fontWeight="600"
      {...rest}
    >
      {children}
      {onClose && (
        <IconButton
          onClick={() => onClose()}
          variant="ghost"
          iconName="X"
          aria-label={t("Close modal", { ns: "common" })}
          size="sm"
          iconSize="element.xl"
          data-testid="modal-close-button"
          colorPalette="link"
          iconColor={closeButtonProps?.iconColor ?? "fg"}
          {...closeButtonProps}
        />
      )}
    </ChakraDialog.Header>
  );
};

Modal.Footer = function Footer(props: ChakraDialogFooterProps): JSX.Element {
  return (
    <ChakraDialog.Footer
      borderTop="1px solid"
      borderColor="border"
      flexDirection="row-reverse"
      justifyContent="space-between"
      padding={{
        base: "md",
        sm: "lg",
      }}
      {...props}
    />
  );
};

Modal.Body = forwardRef<HTMLDivElement, ModalBodyProps>(function Body(
  props,
  ref
): JSX.Element {
  const { hasFooter, padding, p, variant: bodyVariant, ...rest } = props;
  const rootVariant = useContext(ModalVariantContext);
  const resolvedVariant = bodyVariant ?? rootVariant;
  const bg = resolvedVariant === "interactive" ? "bg.subtle" : "bg";

  const paddingValue =
    padding !== undefined || p !== undefined
      ? padding ?? p
      : {
          base: "md",
          sm: "xl",
        };
  return (
    <ChakraDialog.Body
      ref={ref}
      padding={paddingValue}
      fontSize="sm"
      flex="1"
      overflowY="auto"
      bg={bg}
      {...(!hasFooter && {
        borderBottomRadius: "lg",
      })}
      {...rest}
    />
  );
});

Modal.CloseButton = function ModalCloseButton(
  props: UiKitCloseButtonProps &
    Omit<ChakraDialogCloseTriggerProps, "asChild" | "children">
): JSX.Element {
  return (
    <ChakraDialog.CloseTrigger
      position="absolute"
      top="sm"
      insetEnd="3"
      {...props}
      asChild
    >
      <CloseButton tabIndex={-1} {...props} />
    </ChakraDialog.CloseTrigger>
  );
};
